import React, { useMemo, useState } from 'react';
import { Button, Input, Text } from 'src/@vymo/ui/atoms';
import { List } from 'src/@vymo/ui/blocks';
import Tooltip from 'src/@vymo/ui/blocks/tooltip';
import { ReactComponent as FailedIcon } from 'src/assets/icons/failed.svg';
import { ReactComponent as ApprovedIcon } from 'src/assets/icons/successCircle.svg';
import useContainerSize from 'src/hooks/useContainerSize';
import { TestSuite } from '../types';
import styles from './index.module.scss';

interface Props {
  suites: TestSuite[];
  onSelectSuite: (suite: TestSuite) => void;
}

function TestSuiteList({ suites, onSelectSuite }: Props) {
  const [containerRef, size] = useContainerSize();
  const [filter, setFilter] = useState<'all' | 'failed' | 'approved'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSuites = useMemo(
    () =>
      suites.filter((suite) => {
        const matchesSearch = suite.name
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
        if (!matchesSearch) return false;

        if (filter === 'failed') {
          const passedTests = suite.tests.filter(
            (test) => test.status === 'pass',
          ).length;
          return passedTests !== suite.tests.length;
        }
        if (filter === 'approved') {
          // Show suites where some tests are approved
          return suite.tests.some(
            (test) => test.status === 'pass' && test.approved,
          );
        }
        return true;
      }),
    [suites, searchQuery, filter],
  );

  return (
    <div className={styles.suiteList}>
      <div className={styles.suiteList__header}>
        <Text type="h6">Test Suites</Text>
        <Text type="sublabel">{filteredSuites.length} suites</Text>
      </div>

      <div className={styles.suiteList__filters}>
        <Input
          placeholder="Search test suites..."
          value={searchQuery}
          onChange={(value) => setSearchQuery(String(value))}
          classNames={styles.suiteList__search}
        />

        <div className={styles.suiteList__filters__buttons}>
          <Tooltip
            content={
              filter === 'failed'
                ? 'Showing Failed Suites'
                : 'Show Failed Suites'
            }
            showIcon={false}
          >
            <Button
              className={styles.suiteList__filterCheckbox}
              type={filter === 'failed' ? 'primary' : 'text'}
              variant="danger"
              onClick={() => setFilter(filter === 'failed' ? 'all' : 'failed')}
              iconProps={{
                icon: <FailedIcon />,
                iconPosition: 'left',
              }}
            />
          </Tooltip>
          <Tooltip
            content={
              filter === 'approved'
                ? 'Showing Approved Suites'
                : 'Show Approved Suites'
            }
            showIcon={false}
          >
            <Button
              aria-label="Show Approved"
              className={styles.suiteList__filterCheckbox}
              type={filter === 'approved' ? 'primary' : 'text'}
              variant="success"
              onClick={() =>
                setFilter(filter === 'approved' ? 'all' : 'approved')
              }
              iconProps={{
                icon: <ApprovedIcon />,
                iconPosition: 'left',
              }}
            />
          </Tooltip>
        </div>
      </div>

      <div
        ref={containerRef}
        style={{ height: size.height, overflowY: 'auto' }}
      >
        <List data-test-id="test-suite-list" showSelected>
          {filteredSuites.map((suite) => {
            const passedTests = suite.tests.filter(
              (test) => test.status === 'pass',
            ).length;
            const totalTests = suite.tests.length;
            const status = passedTests === totalTests ? 'success' : 'error';

            // eslint-disable-next-line no-underscore-dangle
            const suiteId = suite._id;

            return (
              <List.Item
                // eslint-disable-next-line no-underscore-dangle
                key={suiteId}
                onItemClick={() => onSelectSuite(suite)}
                className={styles.suiteList__item}
                data-test-id={`test-suite-${suiteId}`}
              >
                <div className={styles.suiteList__item__content}>
                  <div className={styles.suiteList__item__info}>
                    <Text
                      variant={status === 'success' ? 'default' : 'error'}
                      type="label"
                      classNames={styles.suiteList__item__name}
                    >
                      {suite.name}
                    </Text>
                    <Text
                      type="sublabel"
                      classNames={styles.suiteList__item__path}
                    >
                      {suite.path}
                    </Text>
                  </div>
                  <Text type="sublabel" variant={status}>
                    {passedTests}/{totalTests}
                  </Text>
                </div>
              </List.Item>
            );
          })}
        </List>
      </div>
    </div>
  );
}

export default TestSuiteList;
